import TicketStatus from "shared/enums/TicketStatus";
import { ApiBulkTicket, AssignedTicket } from "./types";
import { InvoicePaymentRequestStatus } from "shared/enums/InvoicePaymentRequestStatus";
import groupBy from "lodash/groupBy";
import sum from "lodash/sum";
import orderBy from "lodash/orderBy";

export const bulkTicketSummaryInitialCount = {
  total: 0,
  unassigned: 0,
  pending: 0,
  registered: 0
};

/**
 * @description List of statuses which are applicable in bulk ticket assignment
 */
const ApplicableBulkTicketStatusMap = [
  TicketStatus.TICKET_ACCEPTANCE_PENDING,
  TicketStatus.TICKET_ACCEPTANCE_APPROVAL_PENDING,
  TicketStatus.COMPLETED
];

/**
 *@description  List of statuses which indicate bulk ticket assignment is in progress
 */
export const BulkTicketAssignmentStatusMap = [
  TicketStatus.TICKET_ACCEPTANCE_PENDING,
  TicketStatus.TICKET_ACCEPTANCE_APPROVAL_PENDING
];

export function getAssignedBulkTicket(assignedTickets?: Array<AssignedTicket>) {
  if (assignedTickets) {
    return assignedTickets.filter(ticket => ApplicableBulkTicketStatusMap.includes(ticket.ticketStatus));
  }
  return [];
}

export function getCompletedAssignedBulkTicket(assignedTickets?: Array<AssignedTicket>) {
  if (assignedTickets) {
    return assignedTickets.filter(ticket => ticket.valid && [TicketStatus.COMPLETED].includes(ticket.ticketStatus));
  }
  return [];
}

export function getPendingAssignedBulkTicket(assignedTickets?: Array<AssignedTicket>) {
  if (assignedTickets) {
    return assignedTickets.filter(ticket => BulkTicketAssignmentStatusMap.includes(ticket.ticketStatus));
  }
  return [];
}

export function getInvoicePaymentPendingBulkTicket(tickets?: Array<ApiBulkTicket>) {
  return tickets?.find(
    ticket =>
      ticket.forwardedToInvoicePayer && ticket.invoicePaymentRequest?.status === InvoicePaymentRequestStatus.PENDING
  );
}

export function getBulkTicketPurchaseSummary(data: Array<ApiBulkTicket>) {
  return data
    .filter(bulkTicket => bulkTicket.valid)
    .reduce((previousValue, ticket) => {
      const pending = getPendingAssignedBulkTicket(ticket.assignedTickets).length;
      const registered = getCompletedAssignedBulkTicket(ticket.assignedTickets).length;
      const unassigned = ticket.ticketsPurchased - (registered + pending);
      return {
        total: previousValue.total + ticket.ticketsPurchased,
        pending: previousValue.pending + pending,
        registered: previousValue.registered + registered,
        unassigned: previousValue.unassigned + unassigned
      };
    }, bulkTicketSummaryInitialCount);
}

export function isBulkTicketAssignmentPending(ticketStatus: TicketStatus) {
  return BulkTicketAssignmentStatusMap.includes(ticketStatus);
}

export function getBulkTicketsInfo(data: Array<ApiBulkTicket>) {
  let purchasesByFormType = groupBy(data, "ticketFormName");
  Object.keys(purchasesByFormType).forEach(key => {
    purchasesByFormType[key] = orderBy(purchasesByFormType[key], "purchasedOn", "desc");
  });

  let mapByFormTypeToPurchaseIds: Record<string, Array<string>> = {};
  Object.keys(purchasesByFormType).forEach(key => {
    mapByFormTypeToPurchaseIds[key] = purchasesByFormType[key].filter(item => item.valid).map(item => item.id);
  });

  return {
    getTicketFormNameByBulkTicketId: (bulkTicketId: string) => {
      return {
        ticketFormName: data.find(item => item.id === bulkTicketId)?.ticketFormName
      };
    },
    hasAnyPurchases:
      Object.values(purchasesByFormType)
        .flat()
        .filter(item => item.valid).length > 0,
    purchasesByFormType,
    mapByFormTypeToPurchaseIds,
    purchaseStatsByFormType: Object.keys(purchasesByFormType).reduce(
      (prev: BulkTicketInfoType["purchaseStatsByFormType"], key: string) => {
        const ticketsPurchased = sum(
          purchasesByFormType[key].filter(item => item.valid).map(item => item.ticketsPurchased)
        );
        const ticketsAssigned = sum(
          purchasesByFormType[key].filter(item => item.valid).map(item => item.assignedTickets?.length ?? 0)
        );
        const unAssignedTickets = ticketsPurchased - ticketsAssigned;

        const unAssignedTicketFormIdList =
          purchasesByFormType[key]
            .filter(item => item.valid)
            .map((item: ApiBulkTicket) => {
              if ((item.assignedTickets?.length ?? 0) < item.ticketsPurchased) {
                return item.id;
              }
              return "";
            })
            .filter(Boolean) || [];

        return {
          ...prev,
          [key]: {
            ticketsPurchased,
            ticketsAssigned,
            unAssignedTickets,
            unAssignedTicketFormIdList
          }
        };
      },
      {} as BulkTicketInfoType["purchaseStatsByFormType"]
    )
  };
}

export interface BulkTicketInfoType {
  getTicketFormInfoByAnyBulkTicketId: (bulkTicketId: string) => { ticketFormName?: string };
  hasAnyPurchases: boolean;
  purchasesByFormType: Record<string, Array<ApiBulkTicket>>;
  mapByFormTypeToPurchaseIds: Record<string, Array<string>>;
  purchaseStatsByFormType: Record<
    string,
    {
      ticketsPurchased: number;
      ticketsAssigned: number;
      unAssignedTickets: number;
      unAssignedTicketFormIdList: Array<string>;
    }
  >;
}
